import { Download, AlertTriangle } from 'lucide-react';
import { useCandidates } from '@/hooks/useCandidates';
import { exportCandidatesToXlsx } from '@/lib/exportXlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatBRL, isStale } from '@/lib/utils';
import type { FunnelStatus, CandidateOrigin, CandidateProfile, CandidateWithDetails } from '@/types';

const STATUS_LABELS: Record<FunnelStatus, string> = {
  prospeccao:     'Prospecção',
  r1:             'R1 — Café',
  r2_r3:          'R2/R3',
  proposta:       'Proposta',
  fechado_ganho:  'Ganho',
  fechado_perdido:'Perdido',
};

const ORIGIN_LABELS: Record<CandidateOrigin, string> = {
  linkedin:           'LinkedIn',
  indicacao_interna:  'Indicação',
  evento:             'Evento',
  ativo:              'Ativo',
  headhunter:         'Headhunter',
};

const PROFILE_LABELS: Record<CandidateProfile, string> = {
  farmer:     'Farmer',
  hunter:     'Hunter',
  private:    'Private',
  alta_renda: 'Alta Renda',
};

function HBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-text-secondary w-32 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-px bg-border overflow-visible relative">
        <div
          className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 bg-accent transition-all rounded-sm"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="font-mono text-xs text-text-primary w-6 text-right tabular-nums">{value}</span>
    </div>
  );
}

function computeSummary(candidates: CandidateWithDetails[]) {
  const closedStatuses: FunnelStatus[] = ['fechado_ganho', 'fechado_perdido'];
  const active = candidates.filter((c) => !closedStatuses.includes(c.funnel_status));
  const won    = candidates.filter((c) => c.funnel_status === 'fechado_ganho');
  const lost   = candidates.filter((c) => c.funnel_status === 'fechado_perdido');

  const staleThreshold = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const stale_count = active.filter(
    (c) => !c.last_interaction_date || c.last_interaction_date < staleThreshold,
  ).length;

  const total_estimated_custody_cents = active.reduce((s, c) => s + (c.estimated_custody_cents ?? 0), 0);
  const avg_score = active.length > 0
    ? Math.round(active.reduce((s, c) => s + (c.score ?? 0), 0) / active.length)
    : 0;

  const countBy = <K extends string>(arr: CandidateWithDetails[], key: keyof CandidateWithDetails) => {
    const map = new Map<string, number>();
    for (const c of arr) {
      const v = c[key] as string | undefined;
      if (v) map.set(v, (map.get(v) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([k, count]) => ({ key: k as K, count }));
  };

  return {
    total_active: active.length,
    total_won:    won.length,
    total_lost:   lost.length,
    stale_count,
    total_estimated_custody_cents,
    avg_score,
    by_status:  countBy<FunnelStatus>(candidates, 'funnel_status').map((r) => ({ status: r.key as FunnelStatus, count: r.count })),
    by_origin:  countBy<CandidateOrigin>(active, 'origin').map((r) => ({ origin: r.key as CandidateOrigin, count: r.count })),
    by_profile: countBy<CandidateProfile>(active, 'profile').map((r) => ({ profile: r.key as CandidateProfile, count: r.count })),
  };
}

export function AnalyticsPage() {
  const { data: candidates = [], isLoading, error } = useCandidates();
  const summary = computeSummary(candidates);
  const staleCandidates = candidates.filter((c) => isStale(c.last_interaction_date));

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-sm text-text-muted">Carregando…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-sm text-status-red">{error.message}</span>
      </div>
    );
  }

  const maxStatus  = Math.max(...summary.by_status.map((s) => s.count).concat([1]));
  const maxOrigin  = Math.max(...summary.by_origin.map((o) => o.count).concat([1]));
  const maxProfile = Math.max(...summary.by_profile.map((p) => p.count).concat([1]));

  return (
    <div className="p-6 flex flex-col gap-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-text-primary">Analytics</h1>
        <Button variant="outline" size="sm" onClick={() => exportCandidatesToXlsx(candidates)}>
          <Download className="h-3 w-3" />
          Exportar
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Ativos',            value: summary.total_active },
          { label: 'Custódia Est.',     value: formatBRL(summary.total_estimated_custody_cents) },
          { label: 'Score Médio',       value: summary.avg_score.toFixed(1) },
          { label: 'Sem Contato +10d',  value: summary.stale_count },
        ].map(({ label, value }) => (
          <div key={label} className="rounded border border-border bg-bg-surface px-4 py-3 shadow-card">
            <p className="text-2xs font-semibold uppercase tracking-widest text-text-muted mb-1">{label}</p>
            <p className="font-serif text-2xl text-text-primary leading-none">{value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader><CardTitle>Por Estágio</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-3">
            {summary.by_status.map((item) => (
              <HBar key={item.status} label={STATUS_LABELS[item.status]} value={item.count} max={maxStatus} />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Por Origem</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-3">
            {summary.by_origin.map((item) => (
              <HBar key={item.origin} label={ORIGIN_LABELS[item.origin]} value={item.count} max={maxOrigin} />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Por Perfil</CardTitle></CardHeader>
          <CardContent className="flex flex-col gap-3">
            {summary.by_profile.map((item) => (
              <HBar key={item.profile} label={PROFILE_LABELS[item.profile]} value={item.count} max={maxProfile} />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Stale alert */}
      {staleCandidates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-status-yellow" />
              <span className="text-status-yellow">Sem contato há mais de 10 dias</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col divide-y divide-border">
            {staleCandidates.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm text-text-primary">{c.name}</span>
                  <span className="ml-2 text-xs text-text-muted">{c.current_institution}</span>
                </div>
                <span className="font-mono text-xs text-status-red tabular-nums">
                  {c.last_interaction_date
                    ? `${Math.floor((Date.now() - new Date(c.last_interaction_date).getTime()) / 86400000)}d`
                    : 'Nunca'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
